import {useEffect, useState} from 'react';
import {Activity, Bike, Dumbbell, Heart, Timer, TrendingUp, Waves, Zap} from 'lucide-react';

// Animation duration constants
const ANIMATION = {
    fast: 500,
    medium: 700,
    slow: 1000,
    verySlow: 1500
};

// Sport icon carousel component
const SportIconCarousel: React.FC = () => {
    const icons = [
        {Icon: Activity, label: 'Running'},
        {Icon: Bike, label: 'Cycling'},
        {Icon: Waves, label: 'Swimming'},
        {Icon: Dumbbell, label: 'Strength'},
        {Icon: Heart, label: 'Recovery'},
        {Icon: Timer, label: 'Intervals'},
        {Icon: TrendingUp, label: 'Progress'},
        {Icon: Zap, label: 'Performance'},
    ];

    // Duplicate icons for seamless loop
    const extendedIcons = [...icons, ...icons, ...icons];

    return (
        <div className="w-full overflow-hidden relative py-8">
            {/*/!* Fade edges *!/*/}
            {/*<div*/}
            {/*    className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-bg-1 to-transparent z-10 pointer-events-none"/>*/}
            {/*<div*/}
            {/*    className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-bg-1 to-transparent z-10 pointer-events-none"/>*/}

            {/* Center spotlight effect */}
            <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-full bg-gradient-radial from-primary-1/10 via-transparent to-transparent pointer-events-none"/>

            {/* Scrolling container */}
            <div className="flex gap-6 animate-carousel">
                {extendedIcons.map((item, index) => (
                    <div
                        key={index}
                        className="flex-shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-2xl
              bg-gradient-to-br from-bg-2 to-bg-1
              border border-primary-1/20
              backdrop-blur-sm
              flex flex-col items-center justify-center gap-2
              shadow-lg shadow-primary-1/5
              hover:border-primary-1/40 hover:shadow-primary-1/10
              transition-all duration-300"
                    >
                        <item.Icon
                            size={32}
                            className="text-primary-1 drop-shadow-[0_0_8px_rgba(57,232,145,0.3)]"
                            strokeWidth={1.5}
                        />
                        <span className="text-xs text-primary-2 font-medium opacity-80">
              {item.label}
            </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Word animation component
interface AnimatedWordProps {
    word: string;
    delay: number;
    onComplete?: () => void;
}

const AnimatedWord: React.FC<AnimatedWordProps> = ({word, delay, onComplete}) => {
    const [show, setShow] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShow(true);
            if (onComplete) {
                setTimeout(onComplete, 600);
            }
        }, delay);

        return () => clearTimeout(timer);
    }, [delay, onComplete]);

    return (
        <span
            className={`inline-block transition-all duration-600 ease-out
        ${show
                ? 'translate-y-0 opacity-100 blur-0'
                : 'translate-y-4 opacity-0 blur-sm'
            }`}
        >
      {word}
            {word !== '' && '\u00A0'}
    </span>
    );
};

// Modern circular progress indicator
interface ProgressRingProps {
    progress: number;
}

const ProgressRing: React.FC<ProgressRingProps> = ({progress}) => {
    const radius = 28;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (progress / 100) * circumference;

    return (
        <div className="relative">
            <svg className="w-16 h-16 sm:w-20 sm:h-20 transform -rotate-90" viewBox="0 0 64 64">
                {/* Background circle */}
                <circle
                    cx="32"
                    cy="32"
                    r={radius}
                    stroke="rgba(57, 232, 145, 0.1)"
                    strokeWidth="4"
                    fill="none"
                />
                {/* Progress circle with glow */}
                <circle
                    cx="32"
                    cy="32"
                    r={radius}
                    stroke="url(#gradient)"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    className="transition-all duration-300 ease-out drop-shadow-[0_0_8px_rgba(57,232,145,0.6)]"
                />
                <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#39E891"/>
                        <stop offset="100%" stopColor="#6AEEAD"/>
                    </linearGradient>
                </defs>
            </svg>
            {/* Center dot */}
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-primary-1 animate-pulse"/>
            </div>
        </div>
    );
};

// Pulsing dots loader
const PulsingDots: React.FC = () => {
    return (
        <div className="flex gap-2">
            {[0, 1, 2].map((i) => (
                <div
                    key={i}
                    className="w-2 h-2 rounded-full bg-primary-1 animate-pulse"
                    style={{
                        animationDelay: `${i * 150}ms`,
                    }}
                />
            ))}
        </div>
    );
};

// Main Enhanced Splash Screen Component
interface EnhancedSplashScreenProps {
    // General splash screen props
    isLoading: boolean;
    onAnimationComplete: () => void;
    onLoadingComplete: () => void;

    // Role-specific props (optional)
    name?: string;
    firstName?: string;
    lastName?: string;
    nickname?: string;
    personalisedMessage?: string;
    role?: "coach" | "athlete";
}

const EnhancedSplashScreen: React.FC<EnhancedSplashScreenProps> = ({
                                                                       isLoading,
                                                                       onAnimationComplete,
                                                                       onLoadingComplete,
                                                                       name,
                                                                       firstName,
                                                                       lastName,
                                                                       nickname,
                                                                       personalisedMessage,
                                                                       role,
                                                                   }) => {
    const [fadeOut, setFadeOut] = useState(false);
    const [logoVisible, setLogoVisible] = useState(false);
    const [brandVisible, setBrandVisible] = useState(false);
    const [userInfoVisible, setUserInfoVisible] = useState(false);
    const [carouselVisible, setCarouselVisible] = useState(false);
    const [startTextAnimation, setStartTextAnimation] = useState(false);
    const [textAnimationComplete, setTextAnimationComplete] = useState(false);
    const [progress, setProgress] = useState(0);

    // Determine if this is a personalized splash screen
    const isPersonalized = !!(name || firstName || lastName || nickname || role);

    // Get display name for personalized screen
    const getDisplayName = () => {
        if (nickname?.trim()) return nickname;
        if (firstName || lastName) {
            return `${firstName ?? ""} ${lastName ?? ""}`.trim();
        }
        return name || "";
    };

    const displayName = getDisplayName();
    const capitalizedRole = role ? role.charAt(0).toUpperCase() + role.slice(1) : "";

    // Determine tagline
    const defaultTagline = role === "coach"
        ? "Unlocking Athlete's Potential"
        : "Unlock Your Athletic Potential";

    const tagline = personalisedMessage?.trim() || defaultTagline;
    const words = tagline.split(' ');
    const wordAnimationDelay = 80;

    // Simulate loading progress
    useEffect(() => {
        if (isLoading && textAnimationComplete) {
            const interval = setInterval(() => {
                setProgress((prev) => {
                    if (prev >= 95) return prev;
                    return prev + Math.random() * 10;
                });
            }, 200);

            return () => clearInterval(interval);
        }

        if (!isLoading && textAnimationComplete) {
            const timeout = setTimeout(() => {
                setProgress(100);
            }, 0);

            return () => clearTimeout(timeout);
        }

        return undefined;
    }, [isLoading, textAnimationComplete]);

    // Animation sequence
    useEffect(() => {
        // Logo appears first
        const logoTimer = setTimeout(() => {
            setLogoVisible(true);
        }, 300);

        // Brand name appears
        const brandTimer = setTimeout(() => {
            setBrandVisible(true);
        }, 800);

        // User info appears (if personalized)
        const userInfoTimer = setTimeout(() => {
            if (isPersonalized) {
                setUserInfoVisible(true);
            }
        }, 1300);

        // Carousel appears
        const carouselTimer = setTimeout(() => {
            setCarouselVisible(true);
        }, isPersonalized ? 1600 : 1300);

        // Tagline starts animating
        const textTimer = setTimeout(() => {
            setStartTextAnimation(true);
        }, isPersonalized ? 2100 : 1800);

        return () => {
            clearTimeout(logoTimer);
            clearTimeout(brandTimer);
            clearTimeout(userInfoTimer);
            clearTimeout(carouselTimer);
            clearTimeout(textTimer);
        };
    }, [isPersonalized]);

    // Handle text animation completion
    const handleLastWordComplete = () => {
        const totalAnimationTime = words.length * wordAnimationDelay;
        const completeDelay = totalAnimationTime + 600;

        setTimeout(() => {
            setTextAnimationComplete(true);
            onAnimationComplete();

            if (!isLoading) {
                setFadeOut(true);
                setTimeout(onLoadingComplete, ANIMATION.slow);
            }
        }, completeDelay);
    };

    // Watch for loading to complete after animation
    useEffect(() => {
        if (!isLoading && textAnimationComplete) {
            setFadeOut(true);
            const timer = setTimeout(() => {
                onLoadingComplete();
            }, ANIMATION.slow);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [isLoading, textAnimationComplete, onLoadingComplete]);

    return (
        <div
            className={`fixed inset-0 z-50 overflow-hidden
        bg-gradient-to-br from-bg-1 via-bg-2 to-bg-1
        transition-opacity duration-1000 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
            style={{
                paddingTop: 'env(safe-area-inset-top)',
                paddingBottom: 'env(safe-area-inset-bottom)',
            }}
        >
            {/* Animated gradient overlay */}
            <div
                className="absolute inset-0 bg-gradient-to-br from-primary-1/5 via-transparent to-primary-3/5 animate-gradient-shift"/>

            {/* Main content */}
            <div className="relative h-full flex flex-col">
                {/* Top section with logo, brand name and user info */}
                <div className="flex-shrink-0 flex flex-col items-center justify-center pt-8 sm:pt-12">
                    {/* Logo */}
                    <div
                        className={`w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center mb-4
              transition-all duration-1000 ease-out
              ${logoVisible
                            ? 'scale-100 opacity-100'
                            : 'scale-50 opacity-0'
                        }`}
                    >
                        <div className="relative">
                            {/* Pulsing glow effect */}
                            <div className={`absolute inset-0 rounded-full bg-primary-1/30 blur-xl animate-pulse-glow
                ${logoVisible ? 'opacity-100' : 'opacity-0'}`}
                            />
                            <img
                                src="/head-icon.svg"
                                alt="Qdos"
                                className="relative w-full h-full object-contain drop-shadow-[0_0_20px_rgba(57,232,145,0.3)]"
                            />
                        </div>
                    </div>

                    {/* Brand name and user info */}
                    <div className="text-center space-y-3">
                        {/* Brand name */}
                        <div
                            className={`transition-all duration-700 ease-out delay-100
                ${brandVisible
                                ? 'translate-y-0 opacity-100 blur-0'
                                : 'translate-y-4 opacity-0 blur-sm'
                            }`}
                        >
                            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-1 text-white tracking-tight">
                                Qdos
                            </h1>
                            <h2 className="text-lg sm:text-xl font-semibold text-primary-1 tracking-wider">
                                COACHING
                            </h2>
                        </div>

                        {/* User info (personalized screen) */}
                        {isPersonalized && displayName && (
                            <div
                                className={`transition-all duration-700 ease-out delay-200
                  ${userInfoVisible
                                    ? 'translate-y-0 opacity-100 blur-0'
                                    : 'translate-y-4 opacity-0 blur-sm'
                                }`}
                            >
                                <div className="space-y-2">
                                    <h3 className="text-xl sm:text-2xl font-bold text-white">
                                        {displayName}
                                    </h3>
                                    {capitalizedRole && (
                                        <div
                                            className="inline-block px-3 py-1 rounded-full bg-primary-1/20 border border-primary-1/30">
                                            <p className="text-sm sm:text-base text-primary-1 font-medium">
                                                {capitalizedRole}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Middle section with centered icon carousel */}
                <div className="flex-1 flex items-center justify-center">
                    <div
                        className={`w-full transition-all duration-700 ease-out
              ${carouselVisible
                            ? 'translate-y-0 opacity-100 blur-0'
                            : 'translate-y-4 opacity-0 blur-sm'
                        }`}
                    >
                        <SportIconCarousel/>
                    </div>
                </div>

                {/* Bottom section with animated tagline and loading indicator */}
                <div className="flex-shrink-0 flex flex-col items-center justify-center pb-12 sm:pb-16 gap-8">
                    {/* Animated tagline */}
                    {startTextAnimation && (
                        <div
                            className="text-white text-lg sm:text-xl md:text-2xl font-medium tracking-wide px-4 text-center">
                            {words.map((word, index) => (
                                <AnimatedWord
                                    key={index}
                                    word={word}
                                    delay={index * wordAnimationDelay}
                                    onComplete={index === words.length - 1 ? handleLastWordComplete : undefined}
                                />
                            ))}
                        </div>
                    )}

                    {/* Loading indicator */}
                    {textAnimationComplete && isLoading && (
                        <div
                            className="flex flex-col items-center gap-4 transition-all duration-500 ease-out
                animate-fade-in"
                        >
                            <ProgressRing progress={progress}/>
                            <div className="flex items-center gap-2 text-primary-2 text-sm sm:text-base">
                                <span>Loading</span>
                                <PulsingDots/>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Custom animations styles */}
            <style>{`
        @keyframes carousel {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(-100% / 3));
          }
        }

        @keyframes gradient-shift {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 0.8;
          }
        }

        @keyframes pulse-glow {
          0%, 100% {
            transform: scale(1);
            opacity: 0.5;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.8;
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-carousel {
          animation: carousel 8s linear infinite;
        }

        .animate-carousel:hover {
          animation-play-state: paused;
        }

        .animate-gradient-shift {
          animation: gradient-shift 8s ease-in-out infinite;
        }

        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }

        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }

        .bg-gradient-radial {
          background: radial-gradient(circle, var(--tw-gradient-stops));
        }
      `}</style>
        </div>
    );
};

export default EnhancedSplashScreen;